import { useQuery } from '@tanstack/react-query'

import { dataOrThrow, type GetPortalInvoicesQuery } from '@snootsvet/shared/portal'

import { usePortalApi } from '@/customer/hooks/use-api'

export const invoicesQueryKey = (status?: GetPortalInvoicesQuery['status']) =>
  status ? ['invoices', { status }] : ['invoices']

/**
 * Fetches member invoices (subscription and non-subscription)
 * via `GET /portal/billing/invoices`. Pass `status: 'overdue'` to
 * fetch only overdue invoices.
 */
export function useListInvoices({
  enabled = true,
  status,
}: {
  enabled?: boolean
  status?: GetPortalInvoicesQuery['status']
} = {}) {
  const api = usePortalApi()
  const query = useQuery({
    queryKey: invoicesQueryKey(status),
    queryFn: async () => dataOrThrow(await api.get('/billing/invoices', { query: { status } })),
    enabled,
    // Components using this hook mount/unmount frequently during
    // navigation; a long stale window avoids refetching on every
    // route change. Mutations invalidate the key explicitly.
    staleTime: 10 * 60 * 1000,
  })

  return {
    invoices: query.data?.invoices ?? [],
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  }
}
